import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes, AlertTriangle, Package, ChevronRight, PackageCheck, ArrowUpRight, ShieldCheck } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';
import { getRxkSkus } from '@/lib/rxkSkus';
import { sortProducts } from '@/lib/productOrder';

type Tab = 'stock' | 'products';

interface StockRow {
  sku: string;
  name_en: string | null;
  warehouse: string;
  qty_on_hand: number;
}

interface BundleStockRow {
  bundle_sku: string;
  available_sets: number;
}

interface SerialRow {
  inventory_code: string;
  status: string;
  sold_at: string | null;
  sold_order_id: string | null;
  order_no: string | null;
}

interface LowStockRow {
  sku: string;
  name_en: string | null;
  step_label: string;
  qty_available: number;
  threshold: number;
}

interface ProductRow {
  id: string;
  sku: string;
  step_label: string;
  name_en: string | null;
  name_ko: string | null;
  volume_ml: number | null;
  msrp_bdt: number | null;
  status: string;
  note: string | null;
}

export default function RxkInventory() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('stock');

  const [stock, setStock] = useState<StockRow[]>([]);
  const [bundleStock, setBundleStock] = useState<BundleStockRow[]>([]);
  const [lowStock, setLowStock] = useState<LowStockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<ProductRow[]>([]);

  const [serialCell, setSerialCell] = useState<{ sku: string; warehouse: string; label: string } | null>(null);
  const [serialRows, setSerialRows] = useState<SerialRow[]>([]);
  const [serialLoading, setSerialLoading] = useState(false);

  const openSerialCell = async (sku: string, warehouse: string, label: string) => {
    setSerialCell({ sku, warehouse, label });
    setSerialLoading(true);
    const { data } = await supabase.rpc('core_stock_serials_for_cell', { p_sku: sku, p_warehouse: warehouse });
    setSerialRows((data as SerialRow[] | null) ?? []);
    setSerialLoading(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const RXK_SKUS = await getRxkSkus();
    const [stockRes, bundleStockRes, lowStockRes, productRes] = await Promise.all([
      supabase.from('core_stock_current_v').select('sku, name_en, warehouse, qty_on_hand').in('sku', RXK_SKUS).order('sku'),
      supabase.from('core_bundle_stock_v').select('bundle_sku, available_sets'),
      supabase.from('core_low_stock_v').select('sku, name_en, step_label, qty_available, threshold').in('sku', RXK_SKUS).order('sku'),
      supabase.from('core_products').select('*').in('sku', RXK_SKUS).order('sku'),
    ]);
    setStock(sortProducts(stockRes.data as StockRow[]));
    setBundleStock((bundleStockRes.data as BundleStockRow[]) ?? []);
    setLowStock(sortProducts(lowStockRes.data as LowStockRow[]));
    setProducts(sortProducts(productRes.data as ProductRow[]));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-[22px] font-bold text-ink">Inventory (재고)</h1>
        </div>

        <div className="flex items-center gap-2 border-b border-border-subtle pb-3">
          <button
            onClick={() => setTab('stock')}
            className={`flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-2 rounded-xl cursor-pointer transition-colors ${
              tab === 'stock' ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'
            }`}
          >
            <Boxes className="w-3.5 h-3.5" /> Stock (재고 현황)
          </button>
          <button
            onClick={() => setTab('products')}
            className={`flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-2 rounded-xl cursor-pointer transition-colors ${
              tab === 'products' ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'
            }`}
          >
            <Package className="w-3.5 h-3.5" /> Products (제품 관리)
          </button>
        </div>

        <p className="text-[12px] text-ink-faint">
          View-only here — live from ERP. Manage stock and products in Core ERP → Inventory.
        </p>

        {tab === 'stock' && (
          <div className="space-y-5">
            {!loading && lowStock.length > 0 && (
              <Card className="border-warning-border">
                <CardHeader
                  title="Low Stock Alert (재고 부족 경고)"
                  subtitle={`${lowStock.length} SKU below threshold`}
                  icon={<AlertTriangle className="w-4 h-4 text-warning" />}
                />
                <CardBody className="space-y-1">
                  {lowStock.map((s) => (
                    <div key={s.sku} className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface-muted">
                      <div className="flex items-center gap-2">
                        <p className="text-[12.5px] font-semibold text-ink">{s.name_en || s.sku}</p>
                        <Badge tone="neutral">{s.step_label}</Badge>
                      </div>
                      <p className="text-[12px] text-warning font-semibold">{s.qty_available} / {s.threshold} threshold</p>
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}

            <Card hoverable>
              <CardHeader title="Current Stock" subtitle="RxK SKUs by warehouse" icon={<Boxes className="w-4 h-4" />} />
              <CardBody className="space-y-1">
                {loading ? (
                  <Spinner />
                ) : stock.length === 0 ? (
                  <EmptyState title="No stock movements yet" />
                ) : (
                  stock.map((s, i) => {
                    const bundleQty = bundleStock.find((b) => b.bundle_sku === s.sku)?.available_sets;
                    const isBundle = bundleQty !== undefined;
                    const label = `${s.name_en || s.sku} · ${s.warehouse}`;
                    return (
                      <button
                        key={`${s.sku}-${s.warehouse}-${i}`}
                        type="button"
                        disabled={isBundle}
                        onClick={() => openSerialCell(s.sku, s.warehouse, label)}
                        className="w-full flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface-muted disabled:cursor-default text-left"
                      >
                        <div>
                          <p className="text-[12.5px] font-semibold text-ink">{s.name_en || s.sku}</p>
                          <p className="text-[11px] text-ink-faint">{s.sku} · {isBundle ? 'Virtual (from components)' : s.warehouse}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-[13px] font-bold text-ink tabular-nums">{isBundle ? bundleQty : s.qty_on_hand}</p>
                          {!isBundle && <ChevronRight className="w-3.5 h-3.5 text-ink-faint" />}
                        </div>
                      </button>
                    );
                  })
                )}
              </CardBody>
            </Card>
          </div>
        )}

        {tab === 'products' && (
          <Card hoverable>
            <CardHeader title="RxK Product Line" subtitle={`${products.length} SKUs`} icon={<Package className="w-4 h-4" />} />
            <CardBody className="space-y-1">
              {loading ? (
                <Spinner />
              ) : products.length === 0 ? (
                <EmptyState title="No products found" />
              ) : (
                products.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-surface-muted">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[12.5px] font-semibold text-ink truncate">{p.name_en || p.sku}</p>
                        <Badge tone="neutral">{p.step_label}</Badge>
                        <Badge tone={p.status === 'active' ? 'success' : p.status === 'discontinued' ? 'error' : 'neutral'}>{p.status}</Badge>
                      </div>
                      <p className="text-[11px] text-ink-faint truncate">{p.sku} {p.volume_ml ? `· ${p.volume_ml}ml` : ''}</p>
                    </div>
                    <p className="text-[12.5px] font-bold text-ink tabular-nums flex-shrink-0">৳{(p.msrp_bdt ?? 0).toLocaleString()}</p>
                  </div>
                ))
              )}
            </CardBody>
          </Card>
        )}
      </div>

      {/* Serial drill-down for a Current Stock cell */}
      <Modal
        open={!!serialCell}
        onClose={() => setSerialCell(null)}
        title="Serials"
        subtitle={serialCell?.label}
        icon={<ShieldCheck className="w-4 h-4" />}
        maxWidth="max-w-lg"
      >
        <div className="space-y-1 max-h-[420px] overflow-y-auto -mx-1 px-1">
          {serialLoading ? (
            <Spinner />
          ) : serialRows.length === 0 ? (
            <EmptyState title="No serials in this bucket" />
          ) : (
            serialRows.map((r) => {
              const isSold = r.status === 'sold' && !!r.sold_order_id;
              const tone = r.status === 'sold' ? 'success' : r.status === 'assigned' ? 'info' : 'neutral';
              return (
                <button
                  key={r.inventory_code}
                  type="button"
                  disabled={!isSold}
                  onClick={() => isSold && navigate(`/admin/orders/${r.sold_order_id}`)}
                  className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left disabled:cursor-default hover:enabled:bg-surface-muted"
                >
                  <div className="flex items-center gap-2.5">
                    <p className="text-[12.5px] font-mono font-semibold text-ink tabular-nums">{r.inventory_code}</p>
                    <Badge tone={tone}>{r.status}</Badge>
                  </div>
                  {isSold ? (
                    <span className="flex items-center gap-1 text-[11.5px] font-semibold text-primary-700">
                      {r.order_no}
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </span>
                  ) : (
                    <PackageCheck className="w-3.5 h-3.5 text-ink-faint" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </Modal>
    </AppShell>
  );
}
